import { type TRANSLATION_LOCALES, $i18n_state } from "../SHARED/i18n";

const en = {
  forms: {
    global: {
      email: {
        label: "Email",
        hint: "Please enter the email address used for your registration in the third edition of Designathon.",
      },
      button: {
        valid: "Submit",
        invalid: "Submit",
      },
    },
    attendance: {
      header: "Attendance Confirmation Form",
      brand: "Desiganthon 2025",
      welcome: "Dear",
      gratitude:
        "Thank you for your interest in participating in the third edition of Designathon. Please complete your confirmation by agreeing to the following items and uploading the required document:",
      fields: {
        fullname_ar: {
          label: "Full Name Arabic",
        },
        fullname_en: {
          label: "Full Name English",
        },
        file: {
          label: "Specialization Confirmation",
          instruction:
            "Please attach one of the following documents to verify your eligibility for participation in Designathon 2025: a university degree, experience certificate, personal practitioner’s card, valid university ID card, or portfolio of your work.",
          click: "Click to insert files, or Drag and Drop files here",
          allowed: "Only PDFs allowed",
          size: "Maximum file size is 30MB",
        },
        checkbox_attendance: {
          label: "Attendance Confirmation",
          instruction:
            "I confirm my commitment to attend daily throughout the Designathon 2025, from the opening on 13 February 2025 until the event concludes on 15 February 2025, according to the specified schedule.",
        },
        checkbox_terms: {
          label: "Agreement to Terms and Conditions",
          instruction: (element: (props: { start: string; anchor: string; end: string }) => JSX.Element) =>
            element({
              start: "I agree to all the",
              anchor: "Terms And Conditions",
              end: "governing participation in Designathon 2025 and commit to adhering to them.",
            }),
        },
        checkbox_photo: {
          label: "Photography and Publishing Consent",
          instruction:
            "I consent to being photographed during the event and the use of images and videos for promotional and media purposes related to the event.",
        },
      },
    },
  },
  success: {
    attendance: {
      title: "Your response has been sent.",
      subtitle: "We will contact you after reviewing the attached documents.",
    },
  },

  terms_and_conditions: {
    title: "Designathon 2025 Terms and Conditions",
    agree: "By registering and attending the event, participants acknowledge and agree to these Terms and Conditions.",
    instruction: [
      {
        label: "Introduction",
        data: "These Terms and Conditions govern participation in Designathon 2025, organized by the Architecture and Design Arts Authority in Riyadh, Saudi Arabia (“Event”). By registering and attending, participants agree to comply with these terms.",
      },
      {
        label: "Event Details",
        data: [
          "Event Name: Designathon 2025",
          "Organizer: Architecture and Design Arts Authority",
          "Location: Riyadh, Saudi Arabia",
          "Date: February 13, 2025 - February 15, 2025",
        ],
      },
      {
        label: "Eligibility",
        data: "Participation is open to individuals who meet the eligibility criteria outlined in the event registration. The organizer reserves the right to verify participant credentials at any time.",
      },
      {
        label: "Registration and Entry",
        data: [
          "Participants must complete the registration process before the specified deadline.",
          "Upon acceptance, participants will receive a unique QR code for event access.",
          "Entry to the event venue is restricted to individuals with a valid QR code.",
          "Attendees may be required to present a valid National ID along with their QR code for verification.",
        ],
      },
      {
        label: "Attendance and Conduct",
        data: [
          "Participants must attend the event in full and adhere to the schedule.",
          "All participants are expected to conduct themselves respectfully and professionally. Harassment, discrimination, or inappropriate behavior will not be tolerated.",
          "Participants must follow all instructions given by the event staff and comply with local laws and venue regulations.",
          "The organizer reserves the right to deny entry or remove any participant who violates these terms or engages in inappropriate behavior.",
        ],
      },
      {
        label: "Intellectual Property",
        data: [
          "By participating, participants grant the organizer a non-exclusive, royalty-free license to use their submissions for promotional and educational purposes.",
          "Participants must respect the intellectual property rights of others and refrain from unauthorized use or reproduction of any content.",
        ],
      },
      {
        label: "Confidentiality and Privacy",
        data: [
          "Participants must respect the confidentiality of ideas and projects shared during the event.",
          "The organizer may collect and process personal data in accordance with the event’s privacy policy.",
          "Participant information may be shared with event sponsors or partners.",
        ],
      },
      {
        label: "Photography and Media Release",
        data: [
          "By participating, attendees consent to being photographed, recorded, or filmed for promotional purposes.",
          "The organizer has the right to use images and recordings in marketing materials without additional consent.",
        ],
      },
      {
        label: "Event Modifications and Cancellations",
        data: [
          "The event schedule may be subject to changes. The organizer will make efforts to inform attendees of any modifications.",
          "The organizer is not responsible for any postponement or cancellation of the event due to unforeseen circumstances.",
        ],
      },
      {
        label: "Limitation of Liability",
        data: [
          "The organizer is not liable for any loss, damage, or injury sustained during the event.",
          "Participants are responsible for their personal belongings and equipment.",
          "Participants attend at their own risk, and the organizer assumes no liability for any health-related issues, including illnesses or injuries occurring before, during, or after the event.",
        ],
      },
      {
        label: "Force Majeure",
        data: "The organizer is not liable for any delay, modification, or cancellation due to circumstances beyond its control, including but not limited to natural disasters, wars, health emergencies, or government restrictions.",
      },
      {
        label: "Governing Law and Dispute Resolution",
        data: [
          "These terms are governed by the laws of Saudi Arabia.",
          "Any disputes arising from these terms or the event shall be subject to the exclusive jurisdiction of the courts of Saudi Arabia.",
        ],
      },
      {
        label: "Amendments to Terms and Conditions",
        data: [
          "The organizer reserves the right to modify these terms at any time.",
          "Any updates will be communicated to registered attendees and published on the event website.",
        ],
      },
    ],
  },
};

const translations: Record<TRANSLATION_LOCALES, TRANSLATION_SCHEMA> = {
  en,
  ar: {
    forms: {
      global: {
        email: {
          label: "البريد الالكتروني",
          hint: "الرجاء إدخال البريد الإلكتروني المستخدم في تسجيلكم في النسخة الثالثة من ديزايناثون.",
        },
        button: {
          valid: "إرسال",
          invalid: "إرسال",
        },
      },

      attendance: {
        header: "نموذج تأكيد الحضور",
        brand: "ديزايناثون 2025",
        welcome: "متسابقنا العزيز",
        gratitude:
          "نشكرك على اهتمامك بالمشاركة في النسخة الثالثة من ديزايناثون. يرجى إكمال تأكيدك بالموافقة على البنود التالية وتحميل المستند المطلوب:",
        fields: {
          fullname_ar: {
            label: "الاسم كامل بالعربية",
          },
          fullname_en: {
            label: "الاسم كامل بالانجليزية",
          },
          file: {
            label: "تأكيد التخصص",
            instruction:
              "يرجى إرفاق أحد المستندات التالية للتحقق من أهليتك للمشاركة في Designathon 2025: شهادة جامعية، أو شهادة خبرة، أو بطاقة ممارس شخصي، أو بطاقة هوية جامعية سارية المفعول، أو ملف عرض أعمالك (portfolio).",
            click: "انقر لإدراج الملفات",
            allowed: "يسمح بملفات PDF فقط",
            size: "الحد الأقصى لحجم الملفات هو 30 MB",
          },
          checkbox_attendance: {
            label: "تأكيد الحضور",
            instruction:
              "أؤكد التزامي بالحضور يوميًا طوال فترة ديزايناثون 2025، من الافتتاح في 13 فبراير 2025 حتى اختتام الحدث في 15 فبراير 2025، وفقًا للجدول الزمني المحدد.",
          },
          checkbox_terms: {
            label: "الموافقة على الشروط والأحكام",
            instruction: (element: (props: { start: string; anchor: string; end: string }) => JSX.Element) =>
              element({
                start: "أوافق على جميع",
                anchor: "الشروط والأحكام",
                end: "التي تحكم المشاركة في ديزايناثون 2025، وألتزم بالالتزام بها.",
              }),
          },
          checkbox_photo: {
            label: "الموافقة على التصوير والنشر",
            instruction:
              "أوافق على تصويري أثناء الحدث واستخدام الصور ومقاطع الفيديو لأغراض ترويجية وإعلامية تتعلق بالحدث.",
          },
        },
      },
    },
    success: {
      attendance: {
        title: "تم إرسال ردكم",
        subtitle: "سيتم التواصل معاكم بعد مراجعة المستندات المرفقة",
      },
    },

    terms_and_conditions: {
      title: "شروط وأحكام ديزايناثون 2025",
      agree: "بتسجيلهم وحضورهم الفعالية، يقر المشاركون بموافقتهم على هذه الشروط والأحكام.",
      instruction: [
        {
          label: "المقدمة",
          data: "تحكم هذه الشروط والأحكام المشاركة في ديزايناثون 2025 الذي تنظمه هيئة فنون العمارة والتصميم في الرياض، المملكة العربية السعودية (“الفعالية”). من خلال التسجيل والحضور، يوافق المشاركون على الامتثال لهذه الشروط.",
        },
        {
          label: "تفاصيل الفعالية",
          data: [
            "اسم الفعالية: ديزايناثون 2025",
            "الجهة المنظمة: هيئة فنون العمارة والتصميم",
            "الموقع: الرياض، المملكة العربية السعودية",
            "التاريخ: 13 فبراير 2025 - 15 فبراير 2025",
          ],
        },
        {
          label: "الأهلية",
          data: "المشاركة مفتوحة للأفراد الذين يستوفون معايير الأهلية الموضحة في تسجيل الفعالية. تحتفظ الجهة المنظمة بالحق في التحقق من مؤهلات المشاركين في أي وقت.",
        },
        {
          label: "التسجيل والدخول",
          data: [
            "يجب على المشاركين إكمال عملية التسجيل قبل الموعد النهائي المحدد.",
            "عند القبول، سيتلقى المشاركون رمز QR خاص للدخول إلى الفعالية.",
            "يقتصر دخول موقع الفعالية على الأشخاص الذين لديهم رمز QR صالح.",
            "قد يُطلب من الحضور تقديم بطاقة الهوية الوطنية سارية المفعول مع رمز QR للتحقق.",
          ],
        },
        {
          label: "الحضور والسلوك",
          data: [
            "يجب على المشاركين حضور الفعالية كاملة والالتزام بالجدول الزمني.",
            "يُتوقع من جميع المشاركين التصرف باحترام ومهنية. لن يتم التسامح مع أي تحرش أو تمييز أو سلوك غير لائق.",
            "يجب على المشاركين اتباع جميع التعليمات المقدمة من قبل فريق التنظيم والامتثال للقوانين المحلية ولوائح المكان.",
            "تحتفظ الجهة المنظمة بالحق في رفض دخول أو استبعاد أي مشارك يخالف هذه الشروط أو ينخرط في سلوك غير لائق.",
          ],
        },
        {
          label: "الملكية الفكرية",
          data: [
            "بالمشاركة، يمنح المشاركون الجهة المنظمة ترخيصًا غير حصري وخاليًا من العوائد لاستخدام مشاركاتهم لأغراض ترويجية وتعليمية.",
            "يجب على المشاركين احترام حقوق الملكية الفكرية للآخرين وتجنب الاستخدام غير المصرح به أو نسخ أي محتوى.",
          ],
        },
        {
          label: "السرية والخصوصية",
          data: [
            "يجب على المشاركين احترام سرية الأفكار والمشاريع التي تتم مشاركتها خلال الفعالية.",
            "قد تقوم الجهة المنظمة بجمع ومعالجة البيانات الشخصية وفقًا لسياسة الخصوصية الخاصة بالفعالية.",
            "قد تتم مشاركة معلومات المشاركين مع الرعاة أو الشركاء.",
          ],
        },
        {
          label: "التصوير والإعلام",
          data: [
            "بالمشاركة، يوافق المشاركون على التصوير أو التسجيل أو التوثيق بالفيديو لأغراض ترويجية.",
            "يحق للجهة المنظمة استخدام الصور والتسجيلات في المواد التسويقية دون الحاجة إلى إذن إضافي.",
          ],
        },
        {
          label: "تعديلات أو إلغاء الفعالية",
          data: [
            "قد يتم تعديل جدول الفعالية. ستبذل الجهة المنظمة جهودًا لإبلاغ الحضور بأي تغييرات.",
            "لا تتحمل الجهة المنظمة مسؤولية أي تأجيل أو إلغاء للفعالية بسبب ظروف غير متوقعة.",
          ],
        },
        {
          label: "تحديد المسؤولية",
          data: [
            "لا تتحمل الجهة المنظمة أي مسؤولية عن أي خسائر أو أضرار أو إصابات تحدث خلال الفعالية.",
            "يتحمل المشاركون مسؤولية ممتلكاتهم الشخصية ومعداتهم.",
            "يتحمل المشاركون مسؤولية حضورهم، ولا تتحمل الجهة المنظمة أي مسؤولية عن أي مشكلات صحية، بما في ذلك الأمراض أو الإصابات التي قد تحدث قبل أو أثناء أو بعد الفعالية.",
          ],
        },
        {
          label: "القوة القاهرة",
          data: "لا تتحمل الجهة المنظمة أي مسؤولية عن أي تأخير أو تعديل أو إلغاء بسبب ظروف خارجة عن إرادتها، بما في ذلك الكوارث الطبيعية أو الحروب أو الطوارئ الصحية أو القيود الحكومية.",
        },
        {
          label: "القانون الحاكم وتسوية النزاعات",
          data: [
            "تخضع هذه الشروط لقوانين المملكة العربية السعودية.",
            "أي نزاعات تنشأ عن هذه الشروط أو الفعالية تخضع للاختصاص الحصري لمحاكم المملكة العربية السعودية.",
          ],
        },
        {
          label: "التعديلات على الشروط والأحكام",
          data: [
            "حتفظ الجهة المنظمة بالحق في تعديل هذه الشروط في أي وقت.",
            "سيتم إبلاغ الحضور المسجلين بأي تحديثات ونشرها على موقع الفعالية.",
          ],
        },
      ],
    },
  },
};
export type TRANSLATION_SCHEMA = typeof en;

export const t = () => {
  return translations[$i18n_state.value];
};
